import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { baseUrl } from "@/api/config";
import NotificationContent from "@/components/notification/notificationContent";
import { MESSAGE } from "@/constants/message";
import {
  OperationStatus,
  OperationStatusType,
  OperationType
} from "@/types/dataset";
import { getErrorNotification } from "@/utils/parsingStatus";

import useEventSource from "./useEventSource";

import useLoadingAndToast from "./ui/useLoadingAndToast";
import useToast, { EToastType } from "./ui/useToast";

export interface UseParsingStatusProps {
  onSuccess?: () => void;
}

interface UseParsingStatusReturn {
  isParsingActive: boolean;
  operation: OperationStatus | null;
  setIsParsingActive: React.Dispatch<React.SetStateAction<boolean>>;
  setOperationId: React.Dispatch<React.SetStateAction<string | null>>;
  setIsKmlUpload: React.Dispatch<React.SetStateAction<boolean>>;
}

type UserDataOperationEvents = {
  dataset_operation_status: OperationStatus;
  dataset_detail: unknown;
  "dataset_data:meta": unknown;
  "dataset_data:chunk": unknown;
  "dataset_data:done": { dataset_id: string };
  "stream:reset": void;
  "stream:error": void;
  "stream:done": void;
};

type SuccessOperation = Exclude<
  OperationType,
  | OperationType.EXPORT
  | OperationType.IMPORT
  | OperationType.SAVE_ANALYSIS_RESULT
>;

const PARSING_TITLE: Record<SuccessOperation, string> = {
  [OperationType.IMPORT_ADD]: "Data Added Successfully",
  [OperationType.IMPORT_REPLACE]: "All Data is Now Updated",
  [OperationType.IMPORT_NEW]: "Dataset Uploaded Successfully"
};
const PARSING_MESSAGE: Record<SuccessOperation, string> = {
  [OperationType.IMPORT_ADD]:
    "We’ve added your data, it is now available for analysis.",
  [OperationType.IMPORT_REPLACE]:
    "We’ve successfully replaced all the data in your dataset.",
  [OperationType.IMPORT_NEW]:
    "We’ve added your data, it is now available for analysis."
};

type ShowToast = ReturnType<typeof useToast>["showToast"];

export const showSuccessfulParsingStatusToast = ({
  showToast,
  operation,
  isKmlUpload = false,
  autoClose,
  toastId,
  tourId,
  disableAnimation
}: {
  showToast: ShowToast;
  operation: OperationStatus;
  isKmlUpload?: boolean;
  autoClose?: number | false;
  toastId?: string;
  tourId?: string;
  disableAnimation?: boolean;
}) => {
  const { operation_type: operationType, import_count } = operation;

  const title = isKmlUpload
    ? MESSAGE.KML_UPLOAD_SUCCESS_TITLE
    : PARSING_TITLE[operationType as SuccessOperation];

  let description: string;
  if (isKmlUpload) description = MESSAGE.KML_UPLOAD_SUCCESS_DESCRIPTION;
  else if (import_count?.duplicate !== 0)
    description = MESSAGE.DATASET_UPLOAD_ADD_MORE_DUPLICATE_DESCRIPTION;
  else description = PARSING_MESSAGE[operationType as SuccessOperation];

  showToast({
    id: toastId,
    type: EToastType.SUCCESS,
    autoClose,
    disableAnimation,
    message: (
      <div className="flex flex-col gap-1" data-tourid={tourId}>
        <p className="text-sm font-semibold text-txtHeading">{title}</p>
        <p className="text-sm text-txtHeading">{description}</p>
      </div>
    )
  });
};

export function useParsingStatus({
  onSuccess
}: UseParsingStatusProps): UseParsingStatusReturn {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { showError } = useLoadingAndToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const lastEventID = searchParams.get("lastEventID");

  const [operationId, setOperationId] = useState<string | null>(
    searchParams.get("operationId")
  );
  const [isParsingActive, setIsParsingActive] = useState<boolean>(false);
  const [operation, setOperation] = useState<OperationStatus | null>(null);
  const [isKmlUpload, setIsKmlUpload] = useState<boolean>(false);

  const getOperationStreamUrl = () => {
    const u = new URL(
      `${baseUrl}/v1/datasets/operations/${operationId}/stream`,
      window.location.origin
    );
    if (lastEventID) u.searchParams.set("lastEventID", lastEventID);
    return u.toString();
  };

  const handleFailedOperation = (failedOperation: OperationStatus) => {
    const { operation_type: operationType, message: errorMessage } =
      failedOperation;

    const { title, description } = getErrorNotification(
      errorMessage,
      operationType
    );

    showError(<NotificationContent title={title} description={description} />);

    setSearchParams(new URLSearchParams());
  };

  const handleSuccessfulOperation = (successfulOperation: OperationStatus) => {
    showSuccessfulParsingStatusToast({
      showToast,
      operation: successfulOperation,
      isKmlUpload
    });
  };

  const resetFromServer = () => {
    setSearchParams(new URLSearchParams());
    setIsParsingActive(false);
  };

  useEventSource<UserDataOperationEvents>({
    eventKey: ["dataset_operation_status", { operationId }],
    url: getOperationStreamUrl(),
    enabled: !!operationId,
    parse: JSON.parse,
    closeOn: [
      "dataset_data:done",
      "stream:done",
      "stream:reset",
      "stream:error"
    ],
    onOpen: () => {
      setIsParsingActive(true);
    },

    eventHandlers: {
      // Progress/status updates during the operation
      dataset_operation_status: (data, event) => {
        setSearchParams(
          new URLSearchParams({
            lastEventID: event.id,
            operationId: operationId ?? ""
          })
        );

        setOperation(data);

        if (data.status === OperationStatusType.FAILED) {
          handleFailedOperation(data);
          setIsParsingActive(false);
        }
      },
      dataset_detail: (_, event) => {
        setSearchParams(
          new URLSearchParams({
            lastEventID: event.id,
            operationId: operationId ?? ""
          })
        );
      },
      "dataset_data:meta": (_, event) => {
        setSearchParams(
          new URLSearchParams({
            lastEventID: event.id,
            operationId: operationId ?? ""
          })
        );
      },
      "dataset_data:chunk": (_, event) => {
        setSearchParams(
          new URLSearchParams({
            lastEventID: event.id,
            operationId: operationId ?? ""
          })
        );
      },
      // Completion event
      "dataset_data:done": (data) => {
        onSuccess?.();
        if (
          operation?.status === OperationStatusType.SUCCESS &&
          operation?.message
        ) {
          showToast({
            type: EToastType.WARNING,
            message: (
              <div className="flex flex-col gap-1">
                <p className="text-sm font-semibold text-txtHeading">
                  {MESSAGE.DATASET_UPLOAD_SUCCESS_WITH_WARNING_TITLE}
                </p>
                <p className="text-sm text-txtHeading">{operation.message}</p>
              </div>
            )
          });
        } else if (operation?.status === OperationStatusType.SUCCESS) {
          handleSuccessfulOperation(operation);
        }
        navigate(`/dataset-management/${data.dataset_id}`);
        setIsParsingActive(false);
      },
      // Stream reset from server
      "stream:reset": resetFromServer,
      // Stream done from server
      // Avoid resetting URL/state after a successful operation,
      // as it can override navigation triggered earlier.
      "stream:done": () => {
        if (operation?.status !== OperationStatusType.SUCCESS) {
          resetFromServer();
        }
      },
      // Stream error event from server
      "stream:error": () => {
        setSearchParams(new URLSearchParams());

        showToast({
          type: EToastType.ERROR,
          message: (
            <div className="flex flex-col gap-1">
              <p className="text-sm font-bold text-txtHeading">
                {MESSAGE.DATASET_UPLOAD_ERROR_TITLE}
              </p>
              <p className="text-sm text-txtHeading">
                {MESSAGE.DATASET_UPLOAD_ERROR_DESCRIPTION}
              </p>
            </div>
          ),
          autoClose: 2000
        });

        setIsParsingActive(false);
      }
    }
  });

  return {
    isParsingActive,
    operation,
    setIsParsingActive,
    setOperationId,
    setIsKmlUpload
  };
}
